// src/components/FamilyHuddleView.jsx

import React, { useState, useRef, useEffect } from 'react';
import { Quote, CheckCircle2, ChevronRight, X, Zap, Users, Heart, RefreshCw } from 'lucide-react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase-config';

// Import specific data for this view
import { familyHuddleContent, FAMILY_HUDDLE_VIDEOS } from '../data/content';

// --- ADVANCED YOUTUBE PLAYER ---
const CustomYouTubePlayer = ({ videoId }) => {
    const containerRef = useRef(null);
    const playerRef = useRef(null);
    const [isEnded, setIsEnded] = useState(false);

    useEffect(() => {
        setIsEnded(false); 
        if (!videoId) return;

        const loadAPI = () => {
            return new Promise((resolve) => {
                if (window.YT && window.YT.Player) {
                    resolve(window.YT);
                    return;
                }
                
                if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
                    const tag = document.createElement('script');
                    tag.src = 'https://www.youtube.com/iframe_api';
                    const firstScriptTag = document.getElementsByTagName('script')[0];
                    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
                }
                
                window.onYouTubeIframeAPIReady = () => resolve(window.YT);
            });
        };

        let player;
        loadAPI().then((YT) => {
            if (!containerRef.current) return;
            
            const el = document.createElement('div');
            el.className = "w-full h-full absolute inset-0";
            containerRef.current.innerHTML = '';
            containerRef.current.appendChild(el);

            player = new YT.Player(el, {
                videoId: videoId,
                playerVars: { 
                    rel: 0, 
                    modestbranding: 1, 
                    playsinline: 1,
                    showinfo: 0
                },
                events: {
                    onStateChange: (event) => {
                        if (event.data === YT.PlayerState.ENDED) {
                            
                            // --- NEW: FORCE EXIT FULLSCREEN ON END ---
                            if (document.fullscreenElement) {
                                document.exitFullscreen().catch(() => {});
                            } else if (document.webkitFullscreenElement) { /* Safari */
                                document.webkitExitFullscreen().catch(() => {});
                            } else if (document.mozFullScreenElement) { /* Firefox */
                                document.mozCancelFullScreen().catch(() => {});
                            } else if (document.msFullscreenElement) { /* IE11 */
                                document.msExitFullscreen().catch(() => {});
                            }

                            setIsEnded(true);
                        } else if (event.data === YT.PlayerState.PLAYING) {
                            setIsEnded(false);
                        }
                    }
                }
            });
            playerRef.current = player;
        });

        return () => {
            if (playerRef.current && playerRef.current.destroy) {
                playerRef.current.destroy();
            }
        };
    }, [videoId]);

    return (
        <div className="relative w-full h-full bg-black rounded-xl overflow-hidden shadow-2xl ring-1 ring-white/10">
            {/* YouTube Iframe mounts here */}
            <div ref={containerRef} className="absolute inset-0 w-full h-full"></div>
            
            {/* The ERI HUB Custom End Screen Overlay */}
            {isEnded && (
                <div className="absolute inset-0 z-50 bg-slate-900 flex flex-col items-center justify-center animate-in fade-in duration-300">
                    <div className="w-16 h-16 md:w-20 md:h-20 bg-indigo-500/10 rounded-full flex items-center justify-center mb-6 border border-indigo-500/20 shadow-[0_0_30px_rgba(99,102,241,0.2)]">
                        <CheckCircle2 size={40} className="text-indigo-500" />
                    </div>
                    <h3 className="text-white text-2xl md:text-3xl font-black mb-8 text-center px-4 tracking-tight">Video Complete</h3>
                    
                    <button 
                        onClick={() => {
                            setIsEnded(false);
                            if (playerRef.current) playerRef.current.playVideo();
                        }} 
                        className="px-6 py-3.5 bg-white/10 hover:bg-white/20 text-white rounded-xl font-bold transition-colors flex items-center justify-center gap-2 border border-white/10"
                    >
                        <RefreshCw size={18} /> Replay
                    </button>
                </div>
            )}
        </div>
    );
};


// currentUser is optional (this view also renders from AthletePortal without it).
// Completed weeks persist to users/{uid}.huddleProgress as { [weekId]: true };
// without a uid the button is a harmless no-op and nothing is saved.
const FamilyHuddleView = ({ currentUser }) => {
    const [view, setView] = useState('orientation');
    const [selectedWeek, setSelectedWeek] = useState(null);
    const [completedMap, setCompletedMap] = useState(() =>
        (currentUser?.huddleProgress && typeof currentUser.huddleProgress === 'object') ? currentUser.huddleProgress : {}
    );
    const [savingWeek, setSavingWeek] = useState(null);

    // Ref for auto-scrolling to content
    const detailRef = useRef(null);

    // Load huddle progress: prefer the streamed profile prop; fall back to a
    // one-shot read of the user doc when the prop doesn't carry it yet.
    useEffect(() => {
        if (currentUser?.huddleProgress && typeof currentUser.huddleProgress === 'object') {
            setCompletedMap(currentUser.huddleProgress);
            return;
        }
        if (!currentUser?.uid) return;
        let cancelled = false;
        (async () => {
            try {
                const snap = await getDoc(doc(db, 'users', currentUser.uid));
                const progress = snap.exists() ? snap.data().huddleProgress : null;
                if (!cancelled && progress && typeof progress === 'object') {
                    setCompletedMap(progress);
                }
            } catch (e) {
                console.error('Error loading huddle progress:', e);
            }
        })();
        return () => { cancelled = true; };
    }, [currentUser?.uid, currentUser?.huddleProgress]);

    const handleToggleCompleted = async (weekId) => {
        if (!currentUser?.uid || savingWeek) return;
        const next = !completedMap[weekId];
        setCompletedMap(prev => ({ ...prev, [weekId]: next }));
        setSavingWeek(weekId);
        try {
            await setDoc(
                doc(db, 'users', currentUser.uid),
                { huddleProgress: { [weekId]: next } },
                { merge: true }
            );
        } catch (e) {
            console.error('Error saving huddle progress:', e);
        } finally {
            setSavingWeek(null);
        }
    };

    // Effect: Scroll to detail card when a week is selected
    useEffect(() => {
        if (selectedWeek && detailRef.current) {
            setTimeout(() => {
                detailRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
        }
    }, [selectedWeek]);

    return (
        <div className="space-y-8 max-w-6xl mx-auto animate-in fade-in duration-500 pb-20">
            
            {/* Hero Section */}
            <div className="bg-gradient-to-r from-indigo-900 via-blue-900 to-slate-900 rounded-3xl p-8 text-white relative overflow-hidden shadow-2xl">
                <div className="absolute top-1/2 right-0 -translate-y-1/2 translate-x-1/4 opacity-5 pointer-events-none">
                    <Users size={400} />
                </div>
                
                <div className="relative z-10 flex flex-col md:flex-row md:items-end justify-between gap-6">
                    <div>
                        <div className="flex items-center gap-2 mb-2 text-indigo-200 uppercase tracking-widest text-[10px] font-bold">
                            <Heart size={14} className="fill-indigo-200" /> Building Home Culture
                        </div>
                        <h1 className="text-3xl md:text-4xl font-black mb-2 uppercase tracking-tight">Family Huddle</h1>
                        <p className="text-indigo-100 max-w-xl text-base font-medium leading-relaxed">
                            A playbook to build strong connections, trust, and team culture at home.
                        </p>
                    </div>
                    <div className="flex gap-3">
                        <button 
                            onClick={() => setView('orientation')} 
                            className={`px-5 py-2 rounded-full font-bold transition-all text-xs md:text-sm ${view === 'orientation' ? 'bg-white text-indigo-900 shadow-lg' : 'bg-white/10 hover:bg-white/20 text-white border border-white/20'}`}
                        >
                            The Philosophy
                        </button>
                        <button 
                            onClick={() => setView('roadmap')} 
                            className={`px-5 py-2 rounded-full font-bold transition-all text-xs md:text-sm ${view === 'roadmap' ? 'bg-white text-indigo-900 shadow-lg' : 'bg-white/10 hover:bg-white/20 text-white border border-white/20'}`}
                        >
                            8-Week Roadmap
                        </button>
                    </div>
                </div>
            </div>

            {/* View: Orientation / Philosophy */}
            {view === 'orientation' ? (
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start"> 
                    
                    {/* Left Col: Quotes & History (Spans 3/5) */}
                    <div className="lg:col-span-3 space-y-6">
                        <div className="bg-white p-8 rounded-3xl border border-slate-200 shadow-sm">
                            <div className="flex flex-col sm:flex-row gap-4 mb-8">
                                {familyHuddleContent.intro.quotes.map((q, i) => (
                                    <div key={i} className="flex-1 p-5 bg-indigo-50/50 rounded-2xl border-l-4 border-indigo-500">
                                        <p className="italic text-slate-700 font-medium mb-3 text-base leading-snug">"{q.text}"</p>
                                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">— {q.author}</p>
                                    </div>
                                ))}
                            </div>
                            <h2 className="text-xl font-black mb-4 flex items-center gap-3 text-slate-800">
                                <Quote size={24} className="text-indigo-600 fill-indigo-100" /> The Huddle Story
                            </h2>
                            <div className="space-y-4 text-slate-600 text-base leading-relaxed">
                                <p>{familyHuddleContent.intro.history}</p>
                                <p>{familyHuddleContent.intro.purpose}</p>
                            </div>
                        </div>

                        <div className="bg-white p-8 rounded-3xl border border-slate-200 shadow-sm relative overflow-hidden">
                            <div className="relative z-10">
                                <h2 className="text-xl font-black mb-4 flex items-center gap-3 text-slate-800">
                                    <Zap size={24} className="text-yellow-500 fill-yellow-500" /> Team Culture in the Home
                                </h2>
                                <p className="text-slate-600 text-base leading-relaxed mb-6">{familyHuddleContent.intro.culture}</p>
                                <div className="p-5 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-100">
                                    <p className="italic text-blue-800 font-bold text-base text-center leading-relaxed">
                                        "The same ingredients that build a strong culture on a sports organization can be used to build a strong family culture."
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Col: Tips for Success (Spans 2/5) */}
                    <div className="lg:col-span-2 space-y-6">
                        <div className="bg-slate-900 text-white p-8 rounded-3xl shadow-xl border border-slate-800 flex flex-col">
                            <h3 className="font-bold text-xl mb-8 border-b border-slate-700 pb-4 flex items-center gap-3">
                                <CheckCircle2 className="text-green-400" size={24} /> Tips for Success
                            </h3>
                            
                            <ul className="space-y-8">
                                <li className="flex flex-col items-start gap-2">
                                    <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center font-bold text-xs shadow-lg border border-blue-400 mb-1">1</div>
                                    <div>
                                        <p className="font-bold text-base text-white mb-1">Connection over Perfection</p>
                                        <p className="text-slate-400 text-xs leading-relaxed">It takes time to feel comfortable. Don't force it.</p>
                                    </div>
                                </li>
                                <li className="flex flex-col items-start gap-2">
                                    <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center font-bold text-xs shadow-lg border border-blue-400 mb-1">2</div>
                                    <div>
                                        <p className="font-bold text-base text-white mb-1">Parents Lead First</p>
                                        <p className="text-slate-400 text-xs leading-relaxed">Share personally and vulnerably; your kids will follow your lead.</p>
                                    </div>
                                </li>
                                <li className="flex flex-col items-start gap-2">
                                    <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center font-bold text-xs shadow-lg border border-blue-400 mb-1">3</div>
                                    <div>
                                        <p className="font-bold text-base text-white mb-1">Equal Voice</p>
                                        <p className="text-slate-400 text-xs leading-relaxed">Let everyone speak. Ensure nobody dominates the huddle.</p>
                                    </div>
                                </li>
                                <li className="flex flex-col items-start gap-2">
                                    <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center font-bold text-xs shadow-lg border border-blue-400 mb-1">4</div>
                                    <div>
                                        <p className="font-bold text-base text-white mb-1">Zero Distractions</p>
                                        <p className="text-slate-400 text-xs leading-relaxed">No phones. No TV. Just 45 minutes of real presence.</p>
                                    </div>
                                </li>
                            </ul>

                            <button 
                                onClick={() => setView('roadmap')} 
                                className="w-full mt-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl font-black text-lg hover:shadow-xl hover:scale-[1.02] transition-all shadow-md flex items-center justify-center gap-2 group border border-white/10"
                            >
                                START THE PLAYBOOK <ChevronRight size={20} className="group-hover:translate-x-1 transition-transform"/>
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                /* View: Roadmap Grid */
                <div className="space-y-8">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        {familyHuddleContent.weeks.map((w) => (
                            <button 
                                key={w.week}
                                onClick={() => setSelectedWeek(w)}
                                className={`relative p-4 rounded-3xl border-2 transition-all flex flex-col items-center text-center gap-3 group ${selectedWeek?.week === w.week ? 'bg-white border-indigo-600 shadow-xl scale-105 z-10' : 'bg-white border-transparent hover:border-indigo-200 shadow-sm hover:shadow-md'}`}
                            >
                                {completedMap[w.week] && (
                                    <div className="absolute top-2.5 right-2.5 w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center shadow-sm" title="Completed">
                                        <CheckCircle2 size={14} />
                                    </div>
                                )}
                                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-colors ${selectedWeek?.week === w.week ? 'bg-indigo-600 text-white shadow-md' : 'bg-slate-50 text-slate-400 group-hover:bg-indigo-50 group-hover:text-indigo-500'}`}>
                                    {w.icon}
                                </div>
                                <div>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Week {w.week}</p>
                                    <h4 className={`font-black text-sm ${selectedWeek?.week === w.week ? 'text-indigo-900' : 'text-slate-700'}`}>{w.title}</h4>
                                    {completedMap[w.week] && (
                                        <p className="text-[10px] font-bold text-green-600 uppercase tracking-widest mt-1">Completed</p>
                                    )}
                                </div>
                            </button>
                        ))}
                    </div>
                    
                    {/* Week Detail Card */}
                    {selectedWeek && (
                        <div ref={detailRef} className="bg-white rounded-3xl border border-slate-200 shadow-xl overflow-hidden p-6 md:p-10 animate-in slide-in-from-bottom-8 duration-500 scroll-mt-24">
                            <div className="flex justify-between items-start mb-8">
                                <div className="flex items-center gap-5">
                                    <div className="w-16 h-16 bg-indigo-600 text-white rounded-2xl flex items-center justify-center shadow-lg shrink-0">
                                        {selectedWeek.icon}
                                    </div>
                                    <div>
                                        <p className="text-xs font-bold text-indigo-600 uppercase tracking-widest mb-1">The Playbook: Week {selectedWeek.week}</p>
                                        <h3 className="text-2xl font-black text-slate-900">{selectedWeek.title}</h3>
                                    </div>
                                </div>
                                <button onClick={() => setSelectedWeek(null)} className="text-slate-400 hover:text-slate-600 p-2 hover:bg-slate-100 rounded-full transition-colors shrink-0">
                                    <X size={28} />
                                </button>
                            </div>
                            
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
                                {/* Left Column: Video & Activities */}
                                <div className="space-y-8">
                                    {/* --- BULLETPROOF VIDEO EMBED --- */}
                                    {FAMILY_HUDDLE_VIDEOS[selectedWeek.week] && (
                                        <div className="w-full rounded-2xl overflow-hidden shadow-lg bg-black aspect-video relative ring-4 ring-slate-100">
                                            <CustomYouTubePlayer 
                                                videoId={FAMILY_HUDDLE_VIDEOS[selectedWeek.week]} 
                                            />
                                        </div>
                                    )}

                                    <div>
                                        <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2"><Zap size={14}/> The Activity</h4>
                                        <p className="text-slate-800 text-lg font-medium leading-relaxed">{selectedWeek.activity}</p>
                                    </div>
                                    
                                    <div className="bg-indigo-50 p-6 rounded-2xl border border-indigo-100">
                                        <h4 className="text-xs font-black text-indigo-600 uppercase tracking-widest mb-3 flex items-center gap-2">
                                            <CheckCircle2 size={16} /> Key Task
                                        </h4>
                                        <p className="text-indigo-900 text-base leading-relaxed">{selectedWeek.task}</p>
                                    </div>
                                </div>
                                
                                {/* Right Column */}
                                <div className="flex flex-col h-full gap-6">
                                    {selectedWeek.questions && (
                                        <div className="bg-slate-50 p-6 md:p-8 rounded-3xl border border-slate-200 flex-1 flex flex-col">
                                            <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6">
                                                {selectedWeek.listTitle || "Conversation Starters"}
                                            </h4>
                                            <div className="space-y-4">
                                                {selectedWeek.questions.map((q, i) => (
                                                    <div key={i} className="bg-white p-4 rounded-xl border border-slate-200 text-slate-700 text-sm font-medium shadow-sm flex gap-4 items-start">
                                                        <span className="text-indigo-500 font-black text-lg leading-none mt-0.5">{selectedWeek.listIcon || "?"}</span> 
                                                        <span className="flex-1 leading-relaxed">{q}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* Action Button - Locked to Bottom */}
                                    <button
                                        onClick={() => handleToggleCompleted(selectedWeek.week)}
                                        disabled={savingWeek != null}
                                        className={`w-full py-4 mt-auto shrink-0 rounded-2xl font-bold transition-all hover:scale-[1.02] flex items-center justify-center gap-3 shadow-xl text-base disabled:opacity-60 disabled:hover:scale-100 ${completedMap[selectedWeek.week] ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-slate-900 text-white hover:bg-black'}`}
                                    >
                                        <CheckCircle2 size={20} />
                                        {completedMap[selectedWeek.week] ? 'Completed — Tap to Undo' : 'Mark Completed'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default FamilyHuddleView;